import React, { createContext, useContext, useMemo } from "react";
import { Box, Button, IconButton, Typography } from "@material-ui/core";
import DeleteSharpIcon from "@material-ui/icons/DeleteSharp";
import { MTableBody, MTableToolbar } from "material-table";
import {
  mergePinnedZipcodeRenderData,
  zipcodeTableSelectionRefs,
} from "util/zipcodeListGroup";

export const ZipcodesListToolbarContext = createContext(null);

const originalRenderDataRef = { current: [] };

export function ZipcodesListMaterialToolbar(toolbarProps) {
  const ctx = useContext(ZipcodesListToolbarContext);
  if (!ctx) {
    return <MTableToolbar {...toolbarProps} />;
  }
  return <ZipcodesListToolbar toolbarProps={toolbarProps} {...ctx} />;
}

function ZipcodesListMaterialRow({ DefaultRow, pageSize, currentPage, ...rowProps }) {
  const originalIndex = originalRenderDataRef.current.indexOf(rowProps.data);
  const path =
    originalIndex >= 0
      ? [originalIndex + pageSize * currentPage]
      : rowProps.path;

  return <DefaultRow {...rowProps} path={path} />;
}

export function ZipcodesListMaterialBody(bodyProps) {
  const ctx = useContext(ZipcodesListToolbarContext);
  originalRenderDataRef.current = bodyProps.renderData || [];

  const renderData = useMemo(
    () =>
      mergePinnedZipcodeRenderData(
        zipcodeTableSelectionRefs.groupedData,
        bodyProps.renderData,
        zipcodeTableSelectionRefs.pinnedRowIds
      ),
    [bodyProps.renderData, ctx?.selectedRowIdsKey, ctx?.groupedData]
  );

  const { Row: DefaultRow, ...restComponents } = bodyProps.components || {};

  return (
    <MTableBody
      {...bodyProps}
      renderData={renderData}
      components={{
        ...restComponents,
        Row: (rowProps) => (
          <ZipcodesListMaterialRow
            {...rowProps}
            DefaultRow={DefaultRow}
            pageSize={bodyProps.pageSize}
            currentPage={bodyProps.currentPage}
          />
        ),
      }}
    />
  );
}

export const zipcodesTableComponents = {
  Toolbar: ZipcodesListMaterialToolbar,
  Body: ZipcodesListMaterialBody,
};

const buttonStyle = {
  backgroundColor: "#6F9CEB",
  color: "#1F1F1F",
  textTransform: "none",
  fontWeight: 500,
  fontSize: 14,
};

export function ZipcodesListToolbar({
  toolbarProps,
  selectionCount,
  showReassignSupplier,
  showReassignTour,
  showBulkDelete,
  onReassignSupplier,
  onReassignTour,
  onBulkDelete,
  t,
}) {
  const { actions = [], components } = toolbarProps;

  const toolbarActions = useMemo(
    () => actions.filter((action) => !action.isFreeAction),
    [actions]
  );

  const freeActions = useMemo(
    () => actions.filter((action) => action.isFreeAction),
    [actions]
  );

  const ActionsComponent = components?.Actions;

  return (
    <Box className="zipcodes-list-toolbar" display="flex" alignItems="center" width="100%">
      <Box flex={1} minWidth={0}>
        <MTableToolbar
          {...toolbarProps}
          actions={toolbarActions}
          showTextRowsSelected={false}
          showTitle={false}
        />
      </Box>
      <Box
        className="zipcodes-list-toolbar-right"
        display="flex"
        alignItems="center"
        flexShrink={0}
        style={{ gap: 12, paddingRight: 8 }}
      >
        {selectionCount > 0 && (
          <>
            <Typography style={{ color: "#F5F5F5", fontSize: 14, whiteSpace: "nowrap" }}>
              {selectionCount} {t("Selected")}
            </Typography>
            {showReassignSupplier && (
              <Button
                variant="contained"
                disableElevation
                style={buttonStyle}
                onClick={onReassignSupplier}
              >
                {t("Reassign Supplier")}
              </Button>
            )}
            {showReassignTour && (
              <Button
                variant="contained"
                disableElevation
                style={buttonStyle}
                onClick={onReassignTour}
              >
                {t("Reassign Tour")}
              </Button>
            )}
            {showBulkDelete && (
              <IconButton color="primary" onClick={onBulkDelete} aria-label={t("Delete")}>
                <DeleteSharpIcon />
              </IconButton>
            )}
          </>
        )}
        {ActionsComponent && freeActions.length > 0 && (
          <ActionsComponent actions={freeActions} components={components} />
        )}
      </Box>
    </Box>
  );
}
